import {
  FormFieldModel,
  type FormObject,
  type PrepareFieldConfig,
} from "./FormFieldModel";
import { isNil, uniq } from "@/utils/object";
import { BookModelCollection } from "../BookModel/BookModelCollection";
import type {
  LayoutSchema,
} from "@binaryoperations/json-schema-form/core/models";
import type { ConfirmationObjectSchema as ObjectJsonSchema } from "@/components/SchemaForm/schemaDraft";
import type { FormState } from "../../FormManager/FormState";

export class FormFieldModelCollection extends BookModelCollection<
  FormFieldModel,
  FormFieldModelCollection
> {
  preparePrefix(prefix: string | null, index?: number | null | string) {
    const formDataPart =
      prefix === null
        ? prefix
        : prefix.split(".").includes("form_data")
          ? null
          : "form_data";

    return [prefix, formDataPart, "form_field", index]
      .filter((part) => !isNil(part) && part !== "")
      .join(".");
  }

  getAsFormGroups(
    form?: FormObject,

    validationErrors?: Record<string, string[]> | null,
    prefix?: string,
    fieldIndex?: number,
  ) {
    const groups = uniq(
      this.all().map((field) => field.get<string>("presented.group")!),
    );

    return groups.map((group): LayoutSchema => {
      return {
        type: "row",
        options: {
          title: group,
          name: group,
        },
        nodes: this.filter(
          (node) => node.get("presented.group") === group,
        ).buildControls(
          form,
          { validationErrors, prefix, index: fieldIndex },
          undefined,
        ),
      };
    });
  }

  getFieldPaths(prefix: string, index: number) {
    prefix = this.preparePrefix(prefix);
    return this.all().reduce((paths, field) => {
      const fieldPath = [prefix, index, field.getItemName()]
        .filter((part) => !isNil(part))
        .join(".");
      return paths.concat(fieldPath);
    }, [] as string[]);
  }

  buildSchema(form: FormObject, config: PrepareFieldConfig) {
    const allFields = this.all();
    return allFields.reduce(
      (formSchema: ObjectJsonSchema, field): ObjectJsonSchema => {
        const fieldSchema = field.buildFieldSchema(form, config);
        return {
          ...formSchema,
          minProperties: 1,
          required: [
            ...(formSchema.required ?? []),
            ...(fieldSchema.required ?? []),
          ],
          properties: {
            ...formSchema.properties,
            ...fieldSchema.properties,
          },
          confirmFields: {
            ...formSchema.confirmFields,
            ...fieldSchema.confirmFields,
          },
          type: "object",
        };
      },
      {
        type: "object",
        required: [],
        properties: {},
      },
    );
  }

  buildControls(
    form: FormObject,
    config: PrepareFieldConfig,
    sources?: string[],
  ) {
    const validationIndex =
      (form as FormState).participantIndices?.[config.index!] ?? config.index;

    return this.map((field) =>
      field.buildFieldControl(config.prefix ?? "", form, sources, {
        ...config,
        validationPrefix: this.preparePrefix(null, validationIndex),
      }),
    ).flat();
  }
}
